import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Modal, Row, Tab, Tabs, Table } from "react-bootstrap";
import {
  FiAlertTriangle,
  FiClock,
  FiHeadphones,
  FiPhoneCall,
  FiPhoneIncoming,
  FiPhoneOutgoing,
  FiRefreshCw,
  FiTrendingUp,
} from "react-icons/fi";
import { api } from "../services/api";
import PageHeader from "./ui/PageHeader";
import StatCard from "./ui/StatCard";
import DataCard from "./ui/DataCard";
import StatusBadge from "./ui/StatusBadge";
import LoadingState from "./ui/LoadingState";
import AlertBanner from "./ui/AlertBanner";
import CustomerSupport from "./CustomerSupport";

const formatDuration = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
};

const EMPTY_SUMMARY = {
  counts: {},
  byPriority: [],
  byChannel: [],
  byComplaint: [],
  recentTickets: [],
  agentWorkload: [],
};

const SupportOverview = ({ summary }) => {
  const data = summary || EMPTY_SUMMARY;
  const { byPriority = [], byChannel = [], byComplaint = [], recentTickets = [] } = data;

  if (!summary) {
    return (
      <div className="support-empty">
        Overview stats could not be loaded. Ticket Desk and Call Center tabs still work — check that{" "}
        <code>support/summary.php</code> is reachable.
      </div>
    );
  }

  return (
    <div>
      <Row className="g-3 mb-4">
        <Col md={6} lg={4}>
          <DataCard title="Open by priority" padded>
            {byPriority.length === 0 ? (
              <p className="text-muted mb-0 small">No open tickets.</p>
            ) : (
              <div className="support-breakdown">
                {byPriority.map((row) => (
                  <div key={row.priority} className="support-breakdown-row">
                    <span className={`support-priority support-priority-${row.priority}`}>
                      {row.priority.charAt(0).toUpperCase() + row.priority.slice(1)}
                    </span>
                    <div className="support-breakdown-bar">
                      <div
                        className={`support-breakdown-fill priority-${row.priority}`}
                        style={{ width: `${Math.min(100, row.count * 25)}%` }}
                      />
                    </div>
                    <span className="support-breakdown-count">{row.count}</span>
                  </div>
                ))}
              </div>
            )}
          </DataCard>
        </Col>
        <Col md={6} lg={4}>
          <DataCard title="Channel mix" padded>
            {byChannel.length === 0 ? (
              <p className="text-muted mb-0 small">No channel data.</p>
            ) : (
              <div className="support-breakdown">
                {byChannel.map((row) => (
                  <div key={row.channel} className="support-breakdown-row">
                    <span className="support-channel-label">{row.channel}</span>
                    <div className="support-breakdown-bar">
                      <div className="support-breakdown-fill channel" style={{ width: `${Math.min(100, row.count * 20)}%` }} />
                    </div>
                    <span className="support-breakdown-count">{row.count}</span>
                  </div>
                ))}
              </div>
            )}
          </DataCard>
        </Col>
        <Col lg={4}>
          <DataCard title="Top complaint types" padded>
            {byComplaint.length === 0 ? (
              <p className="text-muted mb-0 small">No complaint data.</p>
            ) : (
              <ul className="support-complaint-list mb-0">
                {byComplaint.map((row) => (
                  <li key={row.category}>
                    <span>{row.category}</span>
                    <span className="badge bg-light text-dark">{row.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </DataCard>
        </Col>
      </Row>

      <DataCard title="Recent ticket activity">
        <Table responsive className="billr-table mb-0">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Customer</th>
              <th>Subject</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {recentTickets.map((t) => (
              <tr key={t.ticket_ref}>
                <td><code>{t.ticket_ref}</code></td>
                <td>{t.customer_name}</td>
                <td>{t.subject}</td>
                <td>
                  <span className={`support-priority support-priority-${t.priority}`}>
                    {t.priority.charAt(0).toUpperCase() + t.priority.slice(1)}
                  </span>
                </td>
                <td><StatusBadge status={t.status} label={t.status.replace(/_/g, " ")} /></td>
                <td className="text-muted">{t.created_at}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </DataCard>
    </div>
  );
};

const SupportCalls = ({ onRefreshSummary }) => {
  const [calls, setCalls] = useState([]);
  const [complaintCategories, setComplaintCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [form, setForm] = useState({
    client_user_id: "",
    caller_phone: "",
    agent_code: "",
    complaint_category_id: "",
    call_duration_sec: 0,
    summary: "",
    call_direction: "inbound",
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [callData, userData, agentData] = await Promise.all([
        api.getSupportCalls(),
        api.getUsers(),
        api.getAgents(),
      ]);
      setCalls(callData.calls);
      setComplaintCategories(callData.complaintCategories);
      setUsers(userData);
      setAgents(agentData);
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredCalls = useMemo(() => {
    const q = search.toLowerCase();
    return calls.filter((c) => {
      const matchSearch =
        !q ||
        c.callRef.toLowerCase().includes(q) ||
        c.customerName.toLowerCase().includes(q) ||
        c.callerPhone.includes(q);
      const matchStatus = !filterStatus || c.statusKey === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [calls, search, filterStatus]);

  const handleCreate = async () => {
    if (!form.client_user_id || !form.caller_phone || !form.summary) {
      setFeedback({ type: "warning", message: "Customer, phone and summary required." });
      return;
    }
    setActionLoading("create");
    try {
      await api.createSupportCall(form);
      setShowCreate(false);
      setFeedback({ type: "success", message: "Call logged successfully." });
      loadData();
      onRefreshSummary?.();
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCallAction = async (callRef, action, extra = {}) => {
    setActionLoading(`${callRef}-${action}`);
    try {
      await api.updateSupportCall(callRef, action, extra);
      setFeedback({ type: "success", message: `Call ${callRef} updated.` });
      setSelectedCall(null);
      loadData();
      onRefreshSummary?.();
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <LoadingState message="Loading call center..." />;

  return (
    <div>
      <div className="support-toolbar">
        <div className="support-toolbar-filters">
          <input
            className="billr-input"
            placeholder="Search call ID, customer, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className="billr-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="answered">Answered</option>
            <option value="callback_scheduled">Callback scheduled</option>
            <option value="resolved">Resolved</option>
            <option value="missed">Missed</option>
            <option value="voicemail">Voicemail</option>
          </select>
        </div>
        <button className="btn-billr btn-billr-primary" onClick={() => setShowCreate(true)}>
          + Log Call
        </button>
      </div>

      {feedback && (
        <AlertBanner type={feedback.type} message={feedback.message} onClose={() => setFeedback(null)} />
      )}

      <DataCard title={`Call logs (${filteredCalls.length})`}>
        <Table responsive className="billr-table mb-0">
          <thead>
            <tr>
              <th>Call ID</th>
              <th>Time</th>
              <th>Direction</th>
              <th>Customer</th>
              <th>Complaint</th>
              <th>Agent</th>
              <th>Duration</th>
              <th>Ticket</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredCalls.map((call) => (
              <tr key={call.callId} className={call.statusKey === "missed" ? "row-warning" : undefined}>
                <td><code>{call.callRef}</code></td>
                <td className="text-muted small">{call.callTime}</td>
                <td>
                  {call.direction === "Inbound" ? (
                    <FiPhoneIncoming className="text-success" title="Inbound" />
                  ) : (
                    <FiPhoneOutgoing className="text-primary" title="Outbound" />
                  )}
                </td>
                <td>
                  <strong>{call.customerName}</strong>
                  <div className="small text-muted">{call.callerPhone}</div>
                </td>
                <td>{call.complaintType}</td>
                <td>
                  <div>{call.agentCode}</div>
                  {call.agentName !== "-" && <div className="small text-muted">{call.agentName}</div>}
                </td>
                <td>{call.duration}</td>
                <td>{call.ticketRef ? <code>{call.ticketRef}</code> : "—"}</td>
                <td><StatusBadge status={call.statusKey} label={call.status} /></td>
                <td>
                  <button
                    className="btn-billr btn-billr-outline btn-billr-sm"
                    onClick={() => setSelectedCall(call)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </DataCard>

      <Modal show={!!selectedCall} onHide={() => setSelectedCall(null)} centered size="lg">
        {selectedCall && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>Call {selectedCall.callRef}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <div className="support-info-card">
                    <div className="support-info-label">Customer</div>
                    <div className="support-info-value">{selectedCall.customerName}</div>
                    <div className="support-info-sub">Caller: {selectedCall.callerPhone}</div>
                    <div className="support-info-sub">Registered: {selectedCall.registeredPhone}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="support-info-card">
                    <div className="support-info-label">Call details</div>
                    <div className="support-info-sub">{selectedCall.direction} · {selectedCall.duration}</div>
                    <div className="support-info-sub">{selectedCall.callTime}</div>
                    <div className="support-info-sub">Agent: {selectedCall.agentCode}</div>
                    <StatusBadge status={selectedCall.statusKey} label={selectedCall.status} />
                  </div>
                </Col>
              </Row>
              <DataCard title="Summary" padded>
                <p className="mb-0">{selectedCall.summary || "No summary recorded."}</p>
              </DataCard>
              {selectedCall.resolution && (
                <DataCard title="Resolution" padded className="mt-3">
                  <p className="mb-0">{selectedCall.resolution}</p>
                </DataCard>
              )}
              {selectedCall.callbackAt && (
                <div className="support-callback-banner mt-3">
                  <FiClock /> Callback scheduled: {selectedCall.callbackAt}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              {selectedCall.statusKey !== "resolved" && (
                <>
                  <button
                    className="btn-billr btn-billr-outline"
                    disabled={!!actionLoading}
                    onClick={() => handleCallAction(selectedCall.callRef, "schedule_callback")}
                  >
                    Schedule callback
                  </button>
                  <button
                    className="btn-billr btn-billr-primary"
                    disabled={!!actionLoading}
                    onClick={() =>
                      handleCallAction(selectedCall.callRef, "resolve", {
                        resolution: "Resolved from call center",
                      })
                    }
                  >
                    Mark resolved
                  </button>
                </>
              )}
              <button className="btn-billr btn-billr-outline" onClick={() => setSelectedCall(null)}>
                Close
              </button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Log support call</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <label className="small fw-semibold text-muted">Customer</label>
              <select
                className="billr-select"
                value={form.client_user_id}
                onChange={(e) => {
                  const u = users.find((x) => x.id === parseInt(e.target.value, 10));
                  setForm({ ...form, client_user_id: e.target.value, caller_phone: u?.mobile || "" });
                }}
              >
                <option value="">Select</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.mobile})</option>)}
              </select>
            </Col>
            <Col md={6}>
              <label className="small fw-semibold text-muted">Phone</label>
              <input className="billr-input" value={form.caller_phone} onChange={(e) => setForm({ ...form, caller_phone: e.target.value })} />
            </Col>
            <Col md={4}>
              <label className="small fw-semibold text-muted">Direction</label>
              <select className="billr-select" value={form.call_direction} onChange={(e) => setForm({ ...form, call_direction: e.target.value })}>
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
              </select>
            </Col>
            <Col md={4}>
              <label className="small fw-semibold text-muted">Complaint</label>
              <select className="billr-select" value={form.complaint_category_id} onChange={(e) => setForm({ ...form, complaint_category_id: e.target.value })}>
                <option value="">Select</option>
                {complaintCategories.map((c) => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
              </select>
            </Col>
            <Col md={4}>
              <label className="small fw-semibold text-muted">Duration (sec)</label>
              <input type="number" className="billr-input" min={0} value={form.call_duration_sec} onChange={(e) => setForm({ ...form, call_duration_sec: parseInt(e.target.value, 10) || 0 })} />
            </Col>
            <Col md={6}>
              <label className="small fw-semibold text-muted">Agent</label>
              <select className="billr-select" value={form.agent_code} onChange={(e) => setForm({ ...form, agent_code: e.target.value })}>
                <option value="">Assign</option>
                {agents.map((a) => <option key={a.staff_code} value={a.staff_code}>{a.staff_code} — {a.full_name}</option>)}
              </select>
            </Col>
            <Col md={12}>
              <label className="small fw-semibold text-muted">Summary</label>
              <textarea className="billr-input" rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn-billr btn-billr-outline" onClick={() => setShowCreate(false)}>Cancel</button>
          <button className="btn-billr btn-billr-primary" disabled={actionLoading === "create"} onClick={handleCreate}>Save call</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const SupportTeam = ({ summary }) => {
  const agents = summary?.agentWorkload || [];

  return (
    <DataCard title="Agent workload">
      <Table responsive className="billr-table mb-0">
        <thead>
          <tr>
            <th>Agent</th>
            <th>Name</th>
            <th>Open</th>
            <th>In progress</th>
            <th>Escalated</th>
            <th>Total active</th>
          </tr>
        </thead>
        <tbody>
          {agents.map((a) => {
            const total = Number(a.open_count) + Number(a.active_count) + Number(a.escalated_count);
            return (
              <tr key={a.staff_code}>
                <td><code>{a.staff_code}</code></td>
                <td>{a.full_name}</td>
                <td>{a.open_count}</td>
                <td>{a.active_count}</td>
                <td>{a.escalated_count > 0 ? <span className="text-danger fw-semibold">{a.escalated_count}</span> : 0}</td>
                <td>
                  <span className={total > 5 ? "text-warning fw-semibold" : ""}>{total}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </DataCard>
  );
};

const CustomerSupportHub = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  const loadSummary = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      setSummary(await api.getSupportSummary());
      setFeedback(null);
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `${err.message} — ensure XAMPP is running and webservices are synced.`,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary(true);
  }, [loadSummary]);

  if (loading) return <LoadingState message="Loading support center..." />;

  const counts = summary?.counts || {};

  return (
    <div>
      <PageHeader
        title="Customer Support"
        subtitle="Ticket desk, call center & SLA monitoring for Indian bill payment support"
        action={
          <button className="btn-billr btn-billr-outline btn-billr-sm" onClick={() => loadSummary(true)}>
            <FiRefreshCw size={14} /> Refresh
          </button>
        }
      />

      {feedback && (
        <AlertBanner type={feedback.type} message={feedback.message} onClose={() => setFeedback(null)} />
      )}

      <div className="support-helpline">
        <div className="support-helpline-main">
          <FiHeadphones size={20} />
          <div>
            <strong>Billr Helpline: {summary?.helpline || "1800-123-4567"}</strong>
            <span className="text-muted ms-2">{summary?.supportHours || "9 AM – 9 PM IST"}</span>
          </div>
        </div>
        {counts.slaAtRisk > 0 && (
          <div className="support-helpline-alert">
            <FiAlertTriangle /> {counts.slaAtRisk} ticket(s) breaching 24h SLA
          </div>
        )}
      </div>

      <Row className="g-3 mb-4">
        <Col sm={6} lg={3}>
          <StatCard
            icon={<FiAlertTriangle />}
            label="Open tickets"
            value={counts.openTickets ?? 0}
            sub={`${counts.inProgress ?? 0} in progress`}
            accent="#6366f1"
          />
        </Col>
        <Col sm={6} lg={3}>
          <StatCard
            icon={<FiTrendingUp />}
            label="Escalated"
            value={counts.escalated ?? 0}
            sub={`${counts.closedToday ?? 0} closed today`}
            accent="#ef4444"
            iconBg="#fef2f2"
          />
        </Col>
        <Col sm={6} lg={3}>
          <StatCard
            icon={<FiPhoneCall />}
            label="Pending calls"
            value={counts.pendingCalls ?? 0}
            sub={`${counts.callsToday ?? 0} today`}
            accent="#f59e0b"
            iconBg="#fffbeb"
          />
        </Col>
        <Col sm={6} lg={3}>
          <StatCard
            icon={<FiClock />}
            label="Avg call duration"
            value={formatDuration(counts.avgCallDurationSec ?? 0)}
            sub={`${counts.callsResolvedToday ?? 0} resolved today`}
            accent="#10b981"
            iconBg="#ecfdf5"
          />
        </Col>
      </Row>

      <Tabs defaultActiveKey="desk" className="billr-tabs mb-4">
        <Tab eventKey="overview" title="Overview">
          <SupportOverview summary={summary} />
        </Tab>
        <Tab eventKey="desk" title="Ticket Desk">
          <CustomerSupport onRefreshSummary={loadSummary} />
        </Tab>
        <Tab eventKey="calls" title="Call Center">
          <SupportCalls onRefreshSummary={loadSummary} />
        </Tab>
        <Tab eventKey="team" title="Team & SLA">
          <Row className="g-3">
            <Col lg={8}>
              <SupportTeam summary={summary} />
            </Col>
            <Col lg={4}>
              <DataCard title="SLA policy" padded>
                <ul className="support-sla-list mb-0">
                  <li><strong>First response:</strong> within 4 hours</li>
                  <li><strong>Resolution target:</strong> 24 hours</li>
                  <li><strong>Escalation:</strong> auto-flag after 24h open</li>
                  <li><strong>Priority urgent:</strong> 2 hour response</li>
                </ul>
                <div className="support-sla-stats mt-3">
                  <div className="support-sla-stat">
                    <span>At risk</span>
                    <strong className="text-danger">{counts.slaAtRisk ?? 0}</strong>
                  </div>
                  <div className="support-sla-stat">
                    <span>Open queue</span>
                    <strong>{(counts.openTickets ?? 0) + (counts.inProgress ?? 0)}</strong>
                  </div>
                </div>
              </DataCard>
            </Col>
          </Row>
        </Tab>
      </Tabs>
    </div>
  );
};

export default CustomerSupportHub;
